import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

interface KpiCardProps {
  title: string;
  value: number | string;
  trend?: number | null;
  icon: LucideIcon;
  gradient: string;
  format?: 'number' | 'currency' | 'percent';
  label?: string;
}

function formatValue(value: number | string, fmt: 'number' | 'currency' | 'percent' = 'number') {
  if (typeof value === 'string') return value;
  if (fmt === 'currency') {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
    return value.toFixed(0);
  }
  if (fmt === 'percent') return `${value}%`;
  return value.toLocaleString('en-US');
}

export function KpiCard({ title, value, trend, icon: Icon, gradient, format = 'number', label }: KpiCardProps) {
  const showTrend = trend !== undefined && trend !== null;
  const isPositive = (trend ?? 0) >= 0;
  const TrendIcon = showTrend ? (trend === 0 ? Minus : isPositive ? TrendingUp : TrendingDown) : null;

  return (
    <Card className={`relative overflow-hidden border-0 bg-gradient-to-br ${gradient} text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]`}>
      <CardContent className="pt-4 pb-5 px-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
            <Icon className="h-5 w-5 text-white" />
          </div>
          {showTrend && TrendIcon && (
            <div className={cn('flex items-center gap-0.5 text-xs font-medium px-2 py-0.5 rounded-full bg-white/20', isPositive ? 'text-white' : 'text-white')}>
              <TrendIcon className="h-3 w-3" />
              <span>{Math.abs(trend!)}%</span>
            </div>
          )}
        </div>
        <div className="text-3xl font-bold mb-0.5 tabular-nums">{formatValue(value, format)}</div>
        <div className="text-xs font-medium text-white/85">{title}</div>
        {label && <div className="text-[10px] text-white/60 mt-0.5">{label}</div>}
      </CardContent>
      <div className="absolute -bottom-4 -end-4 h-20 w-20 rounded-full bg-white/10 blur-xl" />
    </Card>
  );
}
